import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import NewColors from "../../themes/NewColors";

function formatGroupLabel(group, referenceGroupId) {
  if (!group) return "Unknown group";
  const d = group.date ? new Date(group.date) : null;
  const refTag =
    referenceGroupId && group.groupId === referenceGroupId
      ? " · Reference photos"
      : "";
  if (!d || Number.isNaN(d.getTime())) {
    return `Unknown date${refTag}`;
  }
  const dateStr = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const timeStr = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateStr} · ${timeStr}${refTag}`;
}

function photoMapFromGroup(group) {
  const m = {};
  if (!group?.photos) return m;
  group.photos.forEach((p) => {
    m[p.type] = p;
  });
  return m;
}

function orderedTypes(groupA, groupB) {
  const types = new Set();
  const config =
    groupA?.photos?.[0]?.configuration ||
    groupB?.photos?.[0]?.configuration ||
    null;
  groupA?.photos?.forEach((p) => types.add(p.type));
  groupB?.photos?.forEach((p) => types.add(p.type));
  const arr = [...types];
  if (config && Array.isArray(config)) {
    return arr.sort((a, b) => {
      const ca = config.find((c) => c.id === a);
      const cb = config.find((c) => c.id === b);
      return (ca?.index ?? 999) - (cb?.index ?? 999);
    });
  }
  return arr.sort();
}

/**
 * Side-by-side comparison for any two progress photo groups (same poses when available).
 * @param {string | null | undefined} referenceGroupId - client's starting/reference group, for labels and shortcuts
 */
const ProgressPhotoCompareAnyTwoDialog = ({
  open,
  onClose,
  groupedPhotos,
  referenceGroupId = null,
}) => {
  const [indexA, setIndexA] = React.useState(0);
  const [indexB, setIndexB] = React.useState(1);

  const groups = Array.isArray(groupedPhotos) ? groupedPhotos : [];

  const referenceIndex = React.useMemo(
    () =>
      referenceGroupId
        ? groups.findIndex((g) => g.groupId === referenceGroupId)
        : -1,
    [groups, referenceGroupId],
  );

  const dialogOpenedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      dialogOpenedRef.current = false;
      return;
    }
    const list = Array.isArray(groupedPhotos) ? groupedPhotos : [];
    if (list.length < 2) {
      setIndexA(0);
      setIndexB(0);
      dialogOpenedRef.current = true;
      return;
    }
    if (!dialogOpenedRef.current) {
      const refIdx = referenceGroupId
        ? list.findIndex((g) => g.groupId === referenceGroupId)
        : -1;
      if (refIdx >= 0) {
        setIndexA(refIdx);
        setIndexB(refIdx === 0 ? 1 : 0);
      } else {
        setIndexA(0);
        setIndexB(1);
      }
      dialogOpenedRef.current = true;
    }
  }, [open, groupedPhotos, referenceGroupId]);

  const groupA = groups[indexA];
  const groupB = groups[indexB];

  const mapA = React.useMemo(() => photoMapFromGroup(groupA), [groupA]);
  const mapB = React.useMemo(() => photoMapFromGroup(groupB), [groupB]);

  const types = React.useMemo(
    () => orderedTypes(groupA, groupB),
    [groupA, groupB],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Compare progress photo groups
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose any two submissions. Poses align when both groups include the same angle.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {groups.length < 2 ? (
          <Typography color="text.secondary">
            At least two photo groups are required to compare.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="compare-group-a">Group A</InputLabel>
                <Select
                  labelId="compare-group-a"
                  label="Group A"
                  value={indexA}
                  onChange={(e) => setIndexA(Number(e.target.value))}
                >
                  {groups.map((g, i) => (
                    <MenuItem key={`a-${g.groupId}-${i}`} value={i}>
                      {formatGroupLabel(g, referenceGroupId)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="compare-group-b">Group B</InputLabel>
                <Select
                  labelId="compare-group-b"
                  label="Group B"
                  value={indexB}
                  onChange={(e) => setIndexB(Number(e.target.value))}
                >
                  {groups.map((g, i) => (
                    <MenuItem key={`b-${g.groupId}-${i}`} value={i}>
                      {formatGroupLabel(g, referenceGroupId)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {referenceIndex >= 0 ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Shortcuts:
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                  onClick={() => setIndexA(referenceIndex)}
                >
                  Reference → Group A
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                  onClick={() => setIndexB(referenceIndex)}
                >
                  Reference → Group B
                </Button>
              </Stack>
            ) : null}

            <Divider />

            {types.map((type) => {
              const pa = mapA[type];
              const pb = mapB[type];
              const label =
                pa?.displayName || pb?.displayName || type;
              if (!pa?.url && !pb?.url) return null;

              return (
                <Box key={type} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {label}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Group A
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              minHeight: 280,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: NewColors.sectionBackground,
                              borderRadius: 1,
                              mt: 1,
                            }}
                          >
                            {pa?.url ? (
                              <img
                                src={pa.url}
                                alt={`${label} A`}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 360,
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No photo for this pose
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Group B
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              minHeight: 280,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: NewColors.sectionBackground,
                              borderRadius: 1,
                              mt: 1,
                            }}
                          >
                            {pb?.url ? (
                              <img
                                src={pb.url}
                                alt={`${label} B`}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 360,
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No photo for this pose
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgressPhotoCompareAnyTwoDialog;
